import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { UserProfile } from '../types/models';
import { authService } from '../services/AuthService';
import { getProfile } from '../services/ApiService';
import {
  AnalyticsEvents, identifyUser, resetAnalytics, track,
} from '../services/AnalyticsService';

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) identifyUser(user);
  }, [user]);

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await getProfile();
      setUser(profile);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      // Auth state lives in httpOnly cookies, unreadable from JS — ask the server directly.
      // A stale/expired access-token cookie triggers a transparent refresh inside getProfile().
      try {
        const profile = await getProfile();
        setUser(profile);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    init();
  }, []);

  const signIn = async (email: string, password: string) => {
    await authService.login({ email, password });
    const profile = await getProfile();
    setUser(profile);
    identifyUser(profile);
    track(AnalyticsEvents.LoggedIn);
  };

  const signUp = async (email: string, password: string, fullName: string, captchaToken?: string) => {
    try {
      await authService.register({ email, password, fullName, captchaToken });
    } catch (err: unknown) {
      track(AnalyticsEvents.SignUpFailed, { reason: err instanceof Error ? err.message : 'unknown' });
      throw err;
    }
    await authService.login({ email, password });
    const profile = await getProfile();
    setUser(profile);
    identifyUser(profile);
    track(AnalyticsEvents.SignedUp);
  };

  const signOut = async () => {
    await authService.logout();
    track(AnalyticsEvents.LoggedOut);
    resetAnalytics();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
